import React, { useEffect, useRef, useState, useCallback } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import CommonSelect, { Option } from "../../../core/common/commonSelect"
import ImageWithBasePath from "../../../core/common/imageWithBasePath"
import Table from "../../../core/common/dataTable/index"
import TooltipOption from "../../../core/common/tooltipOption"
import TransportModal from "./transportModal"
import {
  getAllVans,
  createVan,
  updateVan,
  Van,
} from "../../../service/TransportVehicle/transportVehicle"
import { all_routes } from "../../router/all_routes"
import { toast } from "react-toastify"
import { SORT_OPTIONS } from "../../../core/common/selectoption/selectoption"
import { statusOptions } from "../../../core/common/selectoption/selectoption"
import {
  printTable,
  exportTableToPdf,
  exportTableToExcel,
} from "../../../utils/exportTable"

const EXPORT_HEADERS = [
  "ID",
  "Vehicle No",
  "Contractor",
  "Route",
  "Capacity",
  "Status",
]

const toExportRows = (rows: Van[]): string[][] =>
  rows.map((v: any) => [
    v.vanId || v.id || "",
    v.vehicleNo || v.registration_number || "",
    v.contractorId?.name ?? v.contractor?.name ?? "—",
    Array.isArray(v.route_name)
      ? v.route_name.map((r: any) => r.name ?? r).join(", ")
      : v.route_name || "—",
    String(v.capacity ?? ""),
    v.status,
  ])

const TransportVehicleVans = () => {
  const routes = all_routes
  const currentBranch = useSelector(selectCurrentBranch)
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)
  const [data, setData] = useState<Van[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [printLoading, setPrintLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0])
  const [filters, setFilters] = useState<{ status?: Option }>({})
  const [appliedFilters, setAppliedFilters] = useState<{ status?: string }>({})
  const [vanToEdit, setVanToEdit] = useState<Van | null>(null)

  const fetchVans = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getAllVans({
        searchText,
        status: appliedFilters?.status || "",
        sortBy: sortBy.value.sortBy,
        sortOrder: sortBy.value.sortOrder,
      })

      const mappedVans: Van[] = response.data.map((van: any) => ({
        ...van,
        _id: van._id,
        key: van._id,
        id: van.vanId || van._id,
        registration_number: van.registration_number ?? van.vehicleNo,
        van_type: van.van_type ?? van.vanType,
        model: van.model ?? van.maker,
        capacity: van.capacity,
        route_name: van.route_name,
        driver_id: van.driver?._id,
        driver: van.driver,
        status: van.status,
        vanId: van.vanId,
        branch_id: van.branch_id,
        createdAt: van.createdAt,
        updatedAt: van.updatedAt,
      }))

      setData(mappedVans)
      setTotalCount(response.pagination?.total || mappedVans.length)
    } catch (err) {
      toast.error("Failed to fetch vans")
    } finally {
      setLoading(false)
    }
  }, [
    searchText,
    appliedFilters?.status,
    sortBy.value.sortBy,
    sortBy.value.sortOrder,
  ])

  useEffect(() => {
    fetchVans()
  }, [fetchVans])

  const handleApplyFilters = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
    setAppliedFilters({
      status: filters?.status?.value,
    })
  }

  const handleResetFilters = () => {
    setFilters({})
    setAppliedFilters({})
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
  }

  const handleSortSelect = (item: (typeof SORT_OPTIONS)[0]) => {
    setSortBy(item)
  }

  const handleAddVan = async (vanData: {
    registration_number: string
    van_type?: string
    model: string
    capacity: number
    route_name: { name: string }[]
    driver?: string
    status: string
    contractor?: string
    chassis_number?: string
    engine_number?: string
    shift_routes?: { shiftId: string; routeName: string }[]
    van_number?: string
    make?: string
    year?: number
    color?: string
    gps_tracking_id?: string
  }): Promise<boolean | string> => {
    try {
      const response = await createVan(vanData)

      if (
        response.success ||
        response._id ||
        response.id ||
        response.status === "success"
      ) {
        toast.success("Van added successfully!")

        const closeBtn = document.querySelector(
          '#add_vehicle [data-bs-dismiss="modal"]',
        ) as HTMLElement | null
        closeBtn?.click()

        fetchVans()
        return (response as any)?._id ?? (response as any)?.id ?? true
      } else {
        const errorMessage =
          response.message || response.error || "Failed to add van"
        toast.error(errorMessage)
        return false
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to add van"
      toast.error(errorMessage)
      return false
    }
  }

  const handleEditVan = async (
    vanId: string,
    vanData: {
      registration_number: string
      van_type?: string
      model: string
      capacity: number
      route_name: { name: string }[]
      driver?: string
      status: string
      contractor?: string
      chassis_number?: string
      engine_number?: string
      shift_routes?: { shiftId: string; routeName: string }[]
      vanId?: string
    },
  ): Promise<boolean> => {
    try {
      const response = await updateVan(vanId, vanData)
      if (
        response.success ||
        response._id ||
        response.id ||
        response.status === "success"
      ) {
        toast.success("Van updated successfully!")

        const el = document.getElementById("edit_vehicle")
        if (el) {
          el.setAttribute("data-bs-dismiss", "modal")
          el.click()
          el.removeAttribute("data-bs-dismiss")
        }

        fetchVans()
        return true
      } else {
        const errorMessage =
          response.message || response.error || "Failed to update van"
        toast.error(errorMessage)
        return false
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to update van"
      toast.error(errorMessage)
      return false
    }
  }

  const fetchAllForExport = async (): Promise<Van[]> => {
    const response = await getAllVans({
      searchText,
      status: appliedFilters?.status || "",
      sortBy: sortBy.value.sortBy,
      sortOrder: sortBy.value.sortOrder,
      limit: totalCount || 500,
      page: 1,
    })
    return response.data.map((van: any) => ({
      ...van,
      id: van.vanId || van._id,
      registration_number: van.registration_number ?? van.vehicleNo,
    }))
  }

  const handlePrint = async () => {
    if (!totalCount) {
      toast.warning("No data to print")
      return
    }
    setPrintLoading(true)
    try {
      const all = await fetchAllForExport()
      printTable("Vehicles", EXPORT_HEADERS, toExportRows(all), {
        logoUrl: currentBranch?.blackLogoUrl,
        brandName: campusBrandName(currentBranch),
        branchLabel: campusBranchLabel(currentBranch),
      })
    } catch {
      toast.error("Failed to prepare print data")
    } finally {
      setPrintLoading(false)
    }
  }

  const handleExportPdf = async () => {
    if (!totalCount) {
      toast.warning("No data to export")
      return
    }
    setPdfLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToPdf(
        "Vehicles",
        EXPORT_HEADERS,
        toExportRows(all),
        "vehicles.pdf",
        "landscape",
      )
    } catch {
      toast.error("Failed to export PDF")
    } finally {
      setPdfLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!totalCount) {
      toast.warning("No data to export")
      return
    }
    setExcelLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToExcel(
        all.map((v: any) => ({
          ID: v.vanId || v.id || "",
          "Vehicle No": v.vehicleNo || v.registration_number || "",
          Contractor: v.contractorId?.name ?? v.contractor?.name ?? "—",
          Route: Array.isArray(v.route_name)
            ? v.route_name.map((r: any) => r.name ?? r).join(", ")
            : v.route_name || "—",
          Capacity: v.capacity ?? "",
          Status: v.status,
        })),
        "Vehicles",
        "vehicles.xlsx",
      )
    } catch {
      toast.error("Failed to export Excel")
    } finally {
      setExcelLoading(false)
    }
  }

  const columns = [
    {
      title: "ID",
      dataIndex: "vanId",
      render: (text: string) => (
        <Link to="#" className="link-primary">
          {text}
        </Link>
      ),
      sorter: (a: Van, b: Van) => a.vanId.localeCompare(b.vanId),
    },
    {
      title: "Registration No",
      dataIndex: "registration_number",
      sorter: (a: Van, b: Van) =>
        a.registration_number.localeCompare(b.registration_number),
    },
    {
      title: "Van Type",
      dataIndex: "contractorId",
      render: (_: any, record: any) => {
        const type = record.contractorId?.contractorType ?? ""
        if (type === "private") return "Private Van"
        if (type === "school") return "School Van"
        return "—"
      },
      sorter: (a: any, b: any) =>
        (a.contractorId?.contractorType ?? "").localeCompare(
          b.contractorId?.contractorType ?? "",
        ),
    },
    // {
    //   title: "Model No",
    //   dataIndex: "model",
    //   sorter: (a: Van, b: Van) => a.model.localeCompare(b.model),
    // },

    // {
    //   title: "Capacity",
    //   dataIndex: "capacity",
    //   sorter: (a: Van, b: Van) => a.capacity - b.capacity,
    // },

    {
      title: "Route Name",
      dataIndex: "route_name",
      render: (routeNames: { name: string; _id?: string }[]) => (
        <div>
          {Array.isArray(routeNames)
            ? routeNames.map((route, index) => (
                <span key={index} className="route-tag">
                  {route.name}
                  {index < routeNames.length - 1 ? ", " : ""}
                </span>
              ))
            : "No routes"}
        </div>
      ),
      sorter: (a: Van, b: Van) => {
        const aRoutes = Array.isArray(a.route_name)
          ? a.route_name.map(r => r.name).join(", ")
          : ""
        const bRoutes = Array.isArray(b.route_name)
          ? b.route_name.map(r => r.name).join(", ")
          : ""
        return aRoutes.localeCompare(bRoutes)
      },
    },
    {
      title: "Contractor",
      dataIndex: "contractorId",
      render: (_: any, record: any) => (
        <div>{record.contractorId?.name ?? record.contractor?.name ?? "—"}</div>
      ),
      sorter: (a: any, b: any) =>
        (a.contractorId?.name ?? a.contractor?.name ?? "").localeCompare(
          b.contractorId?.name ?? b.contractor?.name ?? "",
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <span
          className={`badge ${status === "active" ? "badge-soft-success" : "badge-soft-danger"} d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {status === "active" ? "Active" : "Inactive"}
        </span>
      ),
      sorter: (a: Van, b: Van) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Van) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#edit_vehicle"
                    onClick={() => setVanToEdit(record)}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                </li>
                {(record as any).registrationImage && (
                  <li>
                    <a
                      className="dropdown-item rounded-1"
                      href={(record as any).registrationImage}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="ti ti-file-description me-2" />
                      View Registration
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Vans</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Vans
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchVans}
                onPrint={handlePrint}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_vehicle"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Van
                </Link>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Vans List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" /> Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <CommonSelect
                            className="select"
                            options={statusOptions}
                            value={filters?.status}
                            onChange={val =>
                              setFilters({ status: val as Option })
                            }
                          />
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <div
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </div>
                        <div
                          className="btn btn-primary"
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="card-body p-0 py-3">
              <Table
                setSearchText={setSearchText}
                dataSource={data}
                columns={columns}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <TransportModal
        onAddVan={handleAddVan}
        onEditVan={handleEditVan}
        vanToEdit={vanToEdit}
        setVanToEdit={setVanToEdit}
      />
    </>
  )
}

export default TransportVehicleVans
